import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Category, Task, TimePeriod } from '../../types/roadmap';
import { TaskCard } from './TaskCard';
import { Edit2, Trash2, Plus } from 'lucide-react';
import * as Icons from 'lucide-react';
import { getVisibleTimePeriods, getTimePeriodConfig } from '../../utils/timePeriods';

interface RoadmapGridProps {
  categories: Category[];
  tasks: Task[];
  showHistorical: boolean;
  showFuture: boolean;
  onAddTask: (categoryId: string, timePeriod: TimePeriod) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (categoryId: string) => void;
}

const DroppableColumn: React.FC<{
  id: string;
  children: React.ReactNode;
  onAdd: () => void;
}> = ({ id, children, onAdd }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`space-y-2 min-h-[100px] p-2 rounded transition-colors ${
        isOver ? 'bg-blue-100 bg-opacity-20' : ''
      }`}
    >
      {children}
      <button
        onClick={onAdd}
        className="w-full p-2 border-2 border-dashed border-gray-400 rounded text-xs text-gray-400 hover:border-gray-300 hover:text-gray-300 transition-colors flex items-center justify-center gap-1"
      >
        <Plus size={14} />
        Add Task
      </button>
    </div>
  );
};

export const RoadmapGrid: React.FC<RoadmapGridProps> = ({
  categories,
  tasks,
  showHistorical,
  showFuture,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onEditCategory,
  onDeleteCategory,
}) => {
  const visiblePeriods = getVisibleTimePeriods(tasks, showHistorical, showFuture);

  const getTasksForCell = (categoryId: string, timePeriod: TimePeriod) => {
    return tasks
      .filter(task => task.category_id === categoryId && task.time_period === timePeriod)
      .sort((a, b) => a.display_order - b.display_order);
  };

  const getIconComponent = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName];
    return IconComponent || Icons.Box;
  };

  const gridStyle = {
    gridTemplateColumns: `minmax(150px, 1fr) repeat(${visiblePeriods.length}, minmax(0, 1fr))`
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header Row */}
      <div className="mb-6 bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4 rounded">
        <div className="grid gap-4 text-center" style={gridStyle}>
          <div className="font-bold text-lg">Swimlane</div>
          {visiblePeriods.map(period => (
            <div
              key={period.id}
              className={`font-bold text-lg ${period.color.headerBg} rounded p-2`}
            >
              {period.label}
            </div>
          ))}
        </div>
      </div>

      {/* Category Rows */}
      {categories.map((category) => {
        const Icon = getIconComponent(category.icon_name);

        return (
          <div key={category.id} className="mb-4">
            <div className="grid gap-4" style={gridStyle}>
              {/* Category Column */}
              <div className={`bg-gradient-to-r ${category.color} text-white rounded-lg p-4 flex items-center justify-between group`}>
                <div className="flex items-center space-x-3 flex-1">
                  <Icon size={24} />
                  <span className="font-semibold text-sm">{category.title}</span>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onEditCategory(category)}
                    className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                    title="Edit category"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => onDeleteCategory(category.id)}
                    className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                    title="Delete category"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Dynamic Time Period Columns */}
              {visiblePeriods.map(period => (
                <DroppableColumn
                  key={period.id}
                  id={`${category.id}|||${period.id}`}
                  onAdd={() => onAddTask(category.id, period.id)}
                >
                  {getTasksForCell(category.id, period.id).map(task => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onEdit={onEditTask}
                      onDelete={onDeleteTask}
                    />
                  ))}
                </DroppableColumn>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
